import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { autosizeRef, useAutosizeAll } from '../../../../hooks/useAutosizeTextarea';
import { useRequirementKeyboardNav } from '../../../../hooks/useRequirementKeyboardNav';
import { Dropdown } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';

import { UseOutletContextSale } from '../Sale';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { Currency } from '../../../../models/currency.model';
import { InventoryReference } from '../../../../models/inventory.reference.model';
import { ProductDA } from '../../../../services/admin/product.service';
import { legendInvalid, legendIconInvalid, legendInvalidRequired, legendValid, legendIconValid, legendValidRequired, legendValidInvalidRestart } from '../../../../utils/legend.data.util';
import { evaluateLegendListObjectOptional, evaluateLegendMapObjectOptional, evaluateLegendMapObjectRequired } from '../../../../scripts/validate.legend.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { contentReferenceSelected } from '../../../../scripts/list.selected.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { inventoryTypeValue } from '../../../../libraries/inventory.type.library';
import { removeTooltip, uploadTooltip } from '../../../../utils/tooltip.util';
import { suffixLegend } from '../../../../scripts/name.legend.script';
import { calculatePriceUnit, calculateRebate } from '../../../../scripts/financial.calculation.script';
import { discountDescription } from '../../../../libraries/discount.library';
import { ForeignCurrencyState, companyToForeign } from '../../../../components/element/InputForeignCurrency';

import AppLegend from '../../../../components/element/Legend';
import AppInputRequirementUnit from '../../../../components/element/InputRequirementUnit';
import AppRequirementInputPrice from '../../../../components/element/InputRequirementPrice';
import AppInputRequirementTax from '../../../../components/element/InputRequirementTax';

export interface AppSaleFormRequirementsProps {
  date: string,
  currency: Currency | null,
  foreignCurrency?: ForeignCurrencyState,
  requirements: {list: boolean, value: {type: string, description: string, contents: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string}[]}[], valid: {type: boolean, description: boolean, content: boolean, contents: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, rebate_amount: boolean, unit: boolean}[]}[]},
  setRequirements: Dispatch<SetStateAction<{list: boolean, value: {type: string, description: string, contents: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string}[]}[], valid: {type: boolean, description: boolean, content: boolean, contents: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, rebate_amount: boolean, unit: boolean}[]}[]}>>
};

const AppSaleFormRequirements: React.FunctionComponent<AppSaleFormRequirementsProps> = ({date, currency, foreignCurrency, requirements, setRequirements}) => {
  const {companyForUser, settingsForCompany, servicesForCompany, productsForCompany, loadSettingsForCompany} = UseOutletContextSale()
  const {lang} = StateLanguage()

  const [list, setList] = useState<{services: InventoryReference[], products: InventoryReference[]}>({services: [], products: []})
  const [searchService, setSearchService] = useState<{text: string, list: InventoryReference[]}>({text: '', list: []})
  const [searchProduct, setSearchProduct] = useState<{text: string, list: InventoryReference[]}>({text: '', list: []})
  const [searchingProducts, setSearchingProducts] = useState(false)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchAbortRef = useRef<AbortController | null>(null)

  const { highlightedIndex, setHighlightedIndex, resetHighlight, handleKeyDown: handleTextareaKeyDown } = useRequirementKeyboardNav()

  useAutosizeAll('textarea[name="detail"]', [requirements.value])

  const mapVariantsToInventory = (items: any[]): InventoryReference[] => {
    return items.flatMap((item: any) => ((item.variants_data ?? item.variants ?? [item]).map((sub_item: any) => ({
      type: 'product' as const,
      id_service: '',
      id_product: item.id ?? sub_item.id_product ?? '',
      id_variant: sub_item.id ?? '',
      display_name: item.display_name ?? sub_item.display_name ?? '',
      description: sub_item.description_short ?? '',
      code: sub_item.code ?? '',
      price_sale: sub_item.price_sale?.amount !== undefined ? sub_item.price_sale : { amount: sub_item.price_sale ?? 0, tax_rate: 0, tax_code: '' },
      price_purchase: sub_item.price_purchase?.amount !== undefined ? sub_item.price_purchase : { amount: sub_item.price_purchase ?? 0, tax_rate: 0, tax_code: '' },
      unit: sub_item.unit ?? { id: '', name: '' },
      discount: sub_item.discount ?? { type: 'none', value: 0, quantity: 0 },
      stock: sub_item.stock ?? 0,
      photos: sub_item.photos ?? []
    }))))
  }

  const searchProductsServer = (text: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (searchAbortRef.current) searchAbortRef.current.abort()

    if (text.length < 2) {
      setSearchProduct({text, list: list.products})
      setSearchingProducts(false)
      resetHighlight()
      return
    }

    setSearchingProducts(true)
    searchTimerRef.current = setTimeout(async () => {
      if (!companyForUser?.id) return
      const abort = new AbortController()
      searchAbortRef.current = abort
      try {
        const response = await ProductDA.searchProductVariants(companyForUser.id, text, 20, abort.signal)
        if (response.status === 0) return // cancelled
        if (response.status === 200 && response.data) {
          const items = response.data.items ?? response.data
          const mapped = Array.isArray(items) ? mapVariantsToInventory(items) : []
          setSearchProduct({text, list: mapped})
        } else {
          setSearchProduct({text, list: []})
        }
      } catch {
        setSearchProduct({text, list: []})
      } finally {
        setSearchingProducts(false)
        resetHighlight()
      }
    }, 300)
  }

  const handleChangeDescriptionToRequirement = (index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.description = event.target.value
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue})
  }

  const handleChangeDetailContentToRequirement = (index: number, sub_index: number, event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    let text: string = event.target.value
    let listProducts: InventoryReference[] = []
    let listService: InventoryReference[] = []

    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.contents[sub_index].detail = text
      }
      return temp_item
    })

    if (servicesForCompany && tempRequirementsValue[index].type === 'service') {
      if (text.length > 0) {
        for (let service of list.services.flatMap(item => item)) {
          if (service.display_name.toLowerCase().indexOf(text.toLowerCase()) !== -1 || service.code.toLowerCase().indexOf(text.toLowerCase()) !== -1) {
            listService.push(service)
          }
        }
      } else {
        listService = list.services
      }
    }

    if (productsForCompany && tempRequirementsValue[index].type === 'product') {
      searchProductsServer(text)
    }

    setRequirements({...requirements, value: tempRequirementsValue})
    setSearchService({text: text, list: listService})
    resetHighlight()
  }

  const handleChangeSelectDetailContentToRequirement = (index: number, sub_index: number, item: InventoryReference) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.contents[sub_index].detail = formatContentDetail(item)
        temp_item.contents[sub_index].reference = (item.type === 'service') ? item.id_service : item.id_variant
        temp_item.contents[sub_index].price_unit = item.price_sale.amount.toString()
        temp_item.contents[sub_index].tax_included = false
        temp_item.contents[sub_index].tax_code = item.price_sale.tax_code
        temp_item.contents[sub_index].tax_rate = item.price_sale.tax_rate
        temp_item.contents[sub_index].unit = {id: item.unit.id, name: item.unit.name, abbreviation: item.unit.symbol}
        temp_item.contents[sub_index].rebate_type = item.discount.type
        temp_item.contents[sub_index].rebate_value = item.discount.value
        temp_item.contents[sub_index].rebate_quantity = item.discount.quantity
        temp_item.contents[sub_index].rebate_amount = calculateRebate((item.type === 'service') ? item.id_service : item.id_variant, temp_item.contents[sub_index].quantity, item.price_sale.amount.toString(), false, item.price_sale.tax_rate, item.discount.type, item.discount.value, item.discount.quantity, '0')
      }
      return temp_item
    })
    let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.contents[sub_index].detail = true
        temp_item.contents[sub_index].price_unit = true
        temp_item.contents[sub_index].tax = true
        temp_item.contents[sub_index].unit = true
        temp_item.contents[sub_index].rebate_amount = true
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue, valid: tempRequirementsValid})
    legendValidRequired(`legend-requirement-content-detail-valid${suffixLegend(index, sub_index)}`, `legend-requirement-content-detail-required${suffixLegend(index, sub_index)}`)
    legendValidRequired(`legend-requirement-content-price-valid${suffixLegend(index, sub_index)}`, `legend-requirement-content-price-required${suffixLegend(index, sub_index)}`)
    legendValidRequired(`legend-requirement-content-rebate-valid${suffixLegend(index, sub_index)}`, `legend-requirement-content-rebate-required${suffixLegend(index, sub_index)}`)
    legendValid(`legend-requirement-content-tax-required${suffixLegend(index, sub_index)}`)
    legendValid(`legend-requirement-content-unit-required${suffixLegend(index, sub_index)}`)
    uploadTooltip()
  }

  const handleChangeQuantityContentToRequirement = (index: number, sub_index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.contents[sub_index].quantity = event.target.value
        temp_item.contents[sub_index].rebate_amount = calculateRebate(temp_item.contents[sub_index].reference, temp_item.contents[sub_index].quantity, temp_item.contents[sub_index].price_unit, temp_item.contents[sub_index].tax_included, temp_item.contents[sub_index].tax_rate, temp_item.contents[sub_index].rebate_type, temp_item.contents[sub_index].rebate_value, temp_item.contents[sub_index].rebate_quantity, temp_item.contents[sub_index].rebate_amount)
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue})
  }

  const handleChangeRebateContentToRequirement = (index: number, sub_index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.contents[sub_index].rebate_amount = event.target.value
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue})
  }

  const validateDescriptionToRequirement = (index: number) => {
    evaluateLegendListObjectOptional(expressions.text, index, 'description', requirements, setRequirements, `legend-requirement-description-valid${suffixLegend(index)}`)
  }

  const validateDetailContentToRequirement = (index: number, sub_index: number) => {
    evaluateLegendMapObjectRequired(expressions.text, index, sub_index, 'detail', requirements, setRequirements, `legend-requirement-content-detail-valid${suffixLegend(index, sub_index)}`, `legend-requirement-content-detail-required${suffixLegend(index, sub_index)}`)
  }

  const validateQuantityContentToRequirement = (index: number, sub_index: number) => {
    evaluateLegendMapObjectRequired(expressions.quantity, index, sub_index, 'quantity', requirements, setRequirements, `legend-requirement-content-quantity-valid${suffixLegend(index, sub_index)}`, `legend-requirement-content-quantity-required${suffixLegend(index, sub_index)}`)
    validateRebaja(index, sub_index)
  }

  const validateRebateContentToRequirement = (index: number, sub_index: number) => {
    evaluateLegendMapObjectOptional(expressions.currency, index, sub_index, 'rebate_amount', requirements, setRequirements, `legend-requirement-content-rebate-valid${suffixLegend(index, sub_index)}`)
  }

  const executeAddRequirement = (item: string) => {
    if (allowRequirement()) {
      let newRequirimentValue: {type: string, description: string, contents: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string}[]} = {
        type: item,
        description: '',
        contents: [
          {
            detail: '',
            reference: '',
            quantity: '',
            price_unit: '',
            tax_code: '',
            tax_rate: 0,
            tax_included: false,
            rebate_type: '',
            rebate_value: 0,
            rebate_quantity: 0,
            rebate_amount: '',
            unit: ''
          }
        ]
      }
      let newRequirementValid: {type: boolean, description: boolean, content: boolean, contents: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, rebate_amount: boolean, unit: boolean}[]} = {
        type: true,
        description: true,
        content: true,
        contents: [
          {
            detail: false,
            quantity: false,
            price_unit: false,
            tax: false,
            rebate_amount: true,
            unit: true
          }
        ]
      }

      setRequirements({list: true, value: [...requirements.value, newRequirimentValue], valid: [...requirements.valid, newRequirementValid]})

      setTimeout( () => {
        let scroll = document.getElementById(`container-requirement${suffixLegend(requirements.value.length)}`)
        scroll?.scrollIntoView({behavior: 'smooth', block: 'center'})
      }, 100 )

      uploadSearch()
      removeTooltip()
      uploadTooltip()
    }

    if (requirements.value.length === 0 && requirements.valid.length === 0) {
      legendValid('legend-requirements-required')
    }
  }

  const executeAddContentToRequirement = (index: number) => {
    if (allowContentToRequirement(index)) {
      let newContentValue: {detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, rebate_type: string, rebate_value: number, rebate_quantity: number, rebate_amount: string, unit: string} = {
        detail: '',
        reference: '',
        quantity: '',
        price_unit: '',
        tax_code: '',
        tax_rate: 0,
        tax_included: false,
        rebate_type: '',
        rebate_value: 0,
        rebate_quantity: 0,
        rebate_amount: '',
        unit: ''
      }
      let newContentValid: {detail: boolean, quantity: boolean, price_unit: boolean, tax: boolean, rebate_amount: boolean, unit: boolean} = {
        detail: false,
        quantity: false,
        price_unit: false,
        tax: false,
        rebate_amount: true,
        unit: true
      }
      let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
        if (index === temp_index) {
          temp_item.contents.push(newContentValue)
        }
        return temp_item
      })
      let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
        if (index === temp_index) {
          temp_item.contents.push(newContentValid)
          temp_item.content = true
        }
        return temp_item
      })

      setRequirements({...requirements, value: tempRequirementsValue, valid: tempRequirementsValid})

      if (tempRequirementsValue[index].contents.length > 0 && tempRequirementsValid[index].contents.length > 0 && tempRequirementsValid[index].content) {
        legendValid(`legend-requirement-contents-required${suffixLegend(index)}`)
      }

      uploadSearch()
      removeTooltip()
      uploadTooltip()
    }
  }

  const executeRemoveRequirement = (index: number) => {
    let tempRequirementsValue = requirements.value.filter((_, temp_index) => (temp_index !== index))
    let tempRequirementsValid = requirements.valid.filter((_, temp_index) => (temp_index !== index))
    setRequirements({list: (tempRequirementsValue.length > 0 && tempRequirementsValid.length > 0), value: tempRequirementsValue, valid: tempRequirementsValid})

    if (tempRequirementsValue.length === 0 && tempRequirementsValid.length === 0) {
      legendInvalid('legend-requirements-required');
    }

    restartFormRequirement(tempRequirementsValue)
    removeTooltip()
  }

  const executeRemoveContentToRequirement = (index: number, sub_index: number) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.contents = temp_item.contents.filter((_, temp_sub_index) => (temp_sub_index !== sub_index))
      }
      return temp_item
    })
    let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.contents = temp_item.contents.filter((_, temp_sub_index) => (temp_sub_index !== sub_index))
        temp_item.content = temp_item.contents.length > 0
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue, valid: tempRequirementsValid})

    if (tempRequirementsValue[index].contents.length === 0 && tempRequirementsValid[index].contents.length === 0 && !tempRequirementsValid[index].content) {
      legendInvalid(`legend-requirement-contents-required${suffixLegend(index)}`)
    }

    restartFormContentToRequirement(index, tempRequirementsValue)
    removeTooltip()
  }

  const executeRemoveReferenceContentToRequirement = (index: number, sub_index: number) => {
    let tempRequirementsValue = requirements.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        temp_item.contents[sub_index].reference = ''
      }
      return temp_item
    })

    setRequirements({...requirements, value: tempRequirementsValue})
    removeTooltip()
  }

  function restartFormRequirement(items: any[]) {
    for (let i = 0; i < items.length; i++) {
      if (expressions && items[i].description.length > 0) {
        if (expressions.text.test(items[i].description)) {
          legendIconValid(`input-requirement-description${suffixLegend(i)}`, `legend-requirement-description-valid${suffixLegend(i)}`)
        } else {
          legendIconInvalid(`input-requirement-description${suffixLegend(i)}`, `legend-requirement-description-valid${suffixLegend(i)}`)
        }
      }
      if (items[i].contents.length === 0) {
        legendInvalid(`legend-requirement-contents-required${suffixLegend(i)}`)
      } else {
        legendValid(`legend-requirement-description-valid${suffixLegend(i)}`)
        restartFormContentToRequirement(i, items)
      }
    }
  }

  function restartFormContentToRequirement(index: number, items: any[]) {
    for (let i = 0; i < items[index].contents.length; i++) {
      legendValid(`legend-requirement-content-unit-required${suffixLegend(index, i)}`)
      if (items[index].contents[i].tax_code.length > 0) {
        legendValid(`legend-requirement-content-tax-required${suffixLegend(index, i)}`)
      } else {
        legendInvalid(`legend-requirement-content-tax-required${suffixLegend(index, i)}`)
      }
      if (expressions) {
        if (expressions.text.test(items[index].contents[i].detail)) {
          legendValidRequired(`legend-requirement-content-detail-valid${suffixLegend(index, i)}`, `legend-requirement-content-detail-required${suffixLegend(index, i)}`)
        } else {
          legendInvalidRequired(items[index].contents[i].detail, `legend-requirement-content-detail-valid${suffixLegend(index, i)}`, `legend-requirement-content-detail-required${suffixLegend(index, i)}`)
        }
        if (expressions.quantity.test(items[index].contents[i].quantity)) {
          legendValidRequired(`legend-requirement-content-quantity-valid${suffixLegend(index, i)}`, `legend-requirement-content-quantity-required${suffixLegend(index, i)}`)
        } else {
          legendInvalidRequired(items[index].contents[i].quantity, `legend-requirement-content-quantity-valid${suffixLegend(index, i)}`, `legend-requirement-content-quantity-required${suffixLegend(index, i)}`)
        }
        if (expressions.currency.test(items[index].contents[i].price_unit)) {
          legendValidRequired(`legend-requirement-content-price-valid${suffixLegend(index, i)}`, `legend-requirement-content-price-required${suffixLegend(index, i)}`)
        } else {
          legendInvalidRequired(items[index].contents[i].price_unit, `legend-requirement-content-price-valid${suffixLegend(index, i)}`, `legend-requirement-content-price-required${suffixLegend(index, i)}`)
        }
        if (expressions.currency.test(items[index].contents[index].rebate_amount)) {
          legendValidRequired(`legend-requirement-content-rebate-valid${suffixLegend(index, i)}`, `legend-requirement-content-rebate-required${suffixLegend(index, i)}`)
        } else {
          legendInvalidRequired(items[index].contents[index].rebate_amount, `legend-requirement-content-rebate-valid${suffixLegend(index, i)}`, `legend-requirement-content-rebate-required${suffixLegend(index, i)}`)
        }
      }
    }
  }

  function allowRequirement(): boolean {
    let allow: boolean = true

    for (let i = 0; i < requirements.valid.length; i++) {
      if (!requirements.valid[i].type || !requirements.valid[i].description || !requirements.valid[i].content) {
        if (!requirements.valid[i].content) {
          legendInvalid(`legend-requirement-contents-required${suffixLegend(i)}`)
        }
        allow = false
      } else {
        allow = allowContentToRequirement(i)
      }
    }

    return allow
  }

  function allowContentToRequirement(index: number): boolean {
    let allow: boolean = true

    if (requirements.valid[index].contents.length > 0) {
      for (let i = 0; i < requirements.valid[index].contents.length; i++) {
        if (!requirements.valid[index].contents[i].detail || !requirements.valid[index].contents[i].quantity || !requirements.valid[index].contents[i].price_unit || !requirements.valid[index].contents[i].tax || !requirements.valid[index].contents[i].rebate_amount) {
          if (!requirements.valid[index].contents[i].detail) {
            legendInvalidRequired(requirements.value[index].contents[i].detail, `legend-requirement-content-detail-valid${suffixLegend(index, i)}`, `legend-requirement-content-detail-required${suffixLegend(index, i)}`)
          }
          if (!requirements.valid[index].contents[i].quantity) {
            legendInvalidRequired(requirements.value[index].contents[i].quantity, `legend-requirement-content-quantity-valid${suffixLegend(index, i)}`, `legend-requirement-content-quantity-required${suffixLegend(index, i)}`)
          }
          if (!requirements.valid[index].contents[i].price_unit) {
            legendInvalidRequired(requirements.value[index].contents[i].price_unit, `legend-requirement-content-price-valid${suffixLegend(index, i)}`, `legend-requirement-content-price-required${suffixLegend(index, i)}`)
          }
          if (!requirements.valid[index].contents[i].tax) {
            legendInvalid(`legend-requirement-content-tax-required${suffixLegend(index, i)}`)
          }
          if (!requirements.valid[index].contents[i].rebate_amount) {
            legendInvalidRequired(requirements.value[index].contents[i].rebate_amount, `legend-requirement-content-rebate-valid${suffixLegend(index, i)}`, `legend-requirement-content-rebate-required${suffixLegend(index, i)}`)
          }
          allow = false
        }
      }
    }

    return allow
  }

  function calculatorAmount(): {sub_total: number, rebate_total: number, after_rebate_total: number, tax_total: number, total: number, requirement_prices: number[], requirement_content_prices: number[][]} {
    let requirementContentPrices: number[][] = []
    let requirementPrices: number[] = []
    let subTotal: number = 0
    let rebateTotal: number = 0
    let afterRebateTotal: number = 0
    let taxTotal: number = 0
    let total: number = 0

    for (let requirement of requirements.value) {
      let requirementContentPrice: number[] = []
      let requirementPrice: number = 0

      for (let content of requirement.contents) {
        let quantity: number = Number(content.quantity)
        let price: number = calculatePriceUnit(content.price_unit, content.tax_included, content.tax_rate)
        let rebate: number = Number(content.rebate_amount) / quantity
        // Unified tax formula: always computed on HT net after rebate (Art. 17 LTVA)
        // `price` is already HT (via calculatePriceUnit), so this works for both TTC and HT modes
        let tax: number = (price - rebate) * content.tax_rate

        requirementContentPrice.push(quantity * (price + tax - rebate))
        requirementPrice += (quantity * (price + tax- rebate))
        subTotal += (quantity * price)
        rebateTotal += (quantity * rebate)
        afterRebateTotal += (quantity * (price - rebate))
        taxTotal += (quantity * tax)
        total += (quantity * (price + tax- rebate))
      }

      requirementContentPrices.push(requirementContentPrice)
      requirementPrices.push(requirementPrice)
    }

    return {sub_total: subTotal, tax_total: taxTotal, rebate_total: rebateTotal, after_rebate_total: afterRebateTotal, total: total, requirement_prices: requirementPrices, requirement_content_prices: requirementContentPrices}
  }

  function formatContentDetail(content: InventoryReference): string {
    return `${((content.code ?? '').length > 0) ? `${content.code}, ` : ''}${content.display_name ?? ''}${(content.type === 'product' && content.description) ? ` ${content.description}` : ''}`
  }

  function validateRebaja(index: number, sub_index: number) {
    let tempRequirementsValid = requirements.valid.map((temp_item, temp_index) => {
      if (index === temp_index) {
        if ((requirements.value[index].contents[sub_index].reference ?? '').length > 0) {
          temp_item.contents[sub_index].rebate_amount = requirements.value[index].contents[sub_index].rebate_amount.length > 0 
        } else {
          temp_item.contents[sub_index].rebate_amount = requirements.value[index].contents[sub_index].rebate_amount.length === 0 || expressions.currency.test(requirements.value[index].contents[sub_index].rebate_amount)
        }
      }
      return temp_item
    })

    if (tempRequirementsValid[index].contents[sub_index].rebate_amount) {
      legendValid(`legend-requirement-content-rebate-valid${suffixLegend(index, sub_index)}`)
    } else {
      legendValidInvalidRestart(`legend-requirement-content-rebate-valid${suffixLegend(index, sub_index)}`)
    }
  }

  function uploadSearch() {
    setSearchService({text: '', list: list.services})
    setSearchProduct({text: '', list: list.products})
  }

  useEffect( () => {
    let tempServices: InventoryReference[] = (servicesForCompany) ? servicesForCompany.flatMap((item) => { return {
      type: 'service',
      id_service: item.id,
      id_product: '',
      id_variant: '',
      display_name: item.display_name,
      description: item.description,
      code: item.code,
      price_sale: item.price_sale,
      price_purchase: item.price_purchase,
      unit: item.unit,
      discount: item.discount,
      stock: 999,
      photos: item.photos
    }}) : []

    let tempProducts: InventoryReference[] = (productsForCompany) ? productsForCompany.flatMap((item) => ((item.variants_data ?? item.variants ?? []).map((sub_item: any) => { return {
      type: 'product',
      id_service: '',
      id_product: item.id,
      id_variant: sub_item.id ?? '',
      display_name: item.display_name ?? '',
      description: sub_item.description_short ?? '',
      code: sub_item.code ?? '',
      price_sale: sub_item.price_sale?.amount !== undefined ? sub_item.price_sale : { amount: sub_item.price_sale ?? 0, tax_rate: 0, tax_code: '' },
      price_purchase: sub_item.price_purchase?.amount !== undefined ? sub_item.price_purchase : { amount: sub_item.price_purchase ?? 0, tax_rate: 0, tax_code: '' },
      unit: sub_item.unit ?? { id: '', name: '' },
      discount: sub_item.discount ?? { type: 'none', value: 0, quantity: 0 },
      stock: sub_item.stock ?? 0,
      photos: sub_item.photos ?? []
    }}))) : []

    setList({services: tempServices, products: tempProducts})
    setSearchService({text: '', list: tempServices})
    setSearchProduct({text: '', list: tempProducts})

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicesForCompany && productsForCompany])

  useEffect( () => {
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsForCompany])

  return (
    <>
      { companyForUser && settingsForCompany && currency &&
        <div className="card card-flush mb-5">
          <div className="card-header">
            <div className="card-title">
              <h4>{lang.labels.requirements}</h4>
            </div>
            <div className="card-toolbar">
              <div className="d-flex justify-content-end align-items-center">
                <button className="btn btn-sm btn-light-primary me-2" type="button" onClick={() => executeAddRequirement("service")}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-3 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                        <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                        <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.addService}
                  </div>
                </button>
                <button className="btn btn-sm btn-light-primary" type="button" onClick={() => executeAddRequirement("product")}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-3 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                        <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                        <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.addProduct}
                  </div>
                </button>
              </div>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle g-1 mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-250px text-nowrap">{lang.labels.detail}</th>
                      <th className="w-100px text-center text-nowrap">{lang.labels.unitMeasure}</th>
                      <th className="w-100px text-center text-nowrap">{lang.labels.quantity}</th>
                      <th className="w-150px text-center text-nowrap">{lang.labels.unitPrice} ({companyForUser.currency?.code})</th>
                      <th className="w-100px text-center text-nowrap">{lang.labels.taxRate} (%)</th>
                      <th className="w-100px text-center text-nowrap">{lang.labels.rebate} ({companyForUser.currency?.code})</th>
                      <th className="w-100px"></th>
                    </tr>
                  </thead>
                  { requirements.value.length > 0 && requirements.valid.length > 0
                    ?
                    <>
                      { requirements.value.map (( (item, index) => { return (
                        <tbody key={index} id={`container-requirement${suffixLegend(index)}`}>
                          <tr>
                            <td colSpan={7}>
                              <div className="d-flex align-items-start">
                                <div className="d-flex align-items-center text-secondary text-uppercase fw-bold fs-6 mt-3 me-5">
                                  <span className="svg-icon svg-icon-3 me-2">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M13 11H3C2.4 11 2 10.6 2 10V9C2 8.4 2.4 8 3 8H13C13.6 8 14 8.4 14 9V10C14 10.6 13.6 11 13 11ZM22 5V4C22 3.4 21.6 3 21 3H3C2.4 3 2 3.4 2 4V5C2 5.6 2.4 6 3 6H21C21.6 6 22 5.6 22 5Z" fill="black"/>
                                      <path opacity="0.3" d="M21 16H3C2.4 16 2 15.6 2 15V14C2 13.4 2.4 13 3 13H21C21.6 13 22 13.4 22 14V15C22 15.6 21.6 16 21 16ZM14 20V19C14 18.4 13.6 18 13 18H3C2.4 18 2 18.4 2 19V20C2 20.6 2.4 21 3 21H13C13.6 21 14 20.6 14 20Z" fill="black"/>
                                    </svg>
                                  </span>
                                  {inventoryTypeValue(lang, item.type)}
                                </div>
                                <div className="w-100">
                                  <input className="form-control px-3" type="text" autoComplete="off" name="description" placeholder={lang.labels.description} value={item.description} onChange={(event) => handleChangeDescriptionToRequirement(index, event)} onKeyUp={() => validateDescriptionToRequirement(index)} onBlur={() => validateDescriptionToRequirement(index)} />
                                  <AppLegend attribute={{check: requirements.valid[index].description, name: "requirement-description", index: index, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                                </div>
                                <button className="btn btn-icon btn-flex btn-light-primary p-5 ms-2" type="button" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="left" data-bs-original-title={lang.labels.addContent} onClick={() => executeAddContentToRequirement(index)}>
                                  <span className="svg-icon svg-icon-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                                      <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                                      <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                                    </svg>
                                  </span>
                                </button>
                                <button className="btn btn-icon btn-flex btn-light-primary p-3 ms-2" type="button" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="left" data-bs-original-title={lang.labels.removeRequirement} onClick={() => executeRemoveRequirement(index)}>
                                  <span className="svg-icon svg-icon-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                      <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                      <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                    </svg>
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                          { item.contents.map (( (sub_item, sub_index) => { return (
                            <tr key={sub_index}>
                              <td valign="top">
                                <Dropdown>
                                  <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
                                    <span className="selection d-flex bg-light rounded">
                                      <textarea ref={autosizeRef} className="form-control px-2" name="detail" autoComplete="off" rows={1} placeholder={lang.labels.content} value={sub_item.detail} onChange={(event) => handleChangeDetailContentToRequirement(index, sub_index, event)} onKeyDown={(e) => handleTextareaKeyDown(index, sub_index, e)} onKeyUp={() => validateDetailContentToRequirement(index, sub_index)} onBlur={() => validateDetailContentToRequirement(index, sub_index)} />
                                      <span className={`btn btn-icon btn btn-bg-info h-auto w-30px m-2 ms-1 ${(requirements.value[index].contents[sub_index].reference ?? '').length === 0 && "d-none"}`} data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="top" data-bs-original-title={lang.labels.removeReference} onClick={() => executeRemoveReferenceContentToRequirement(index, sub_index)}>
                                        <i className="bi bi-x fs-3 text-secondary" ></i>
                                      </span>
                                    </span>
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                                    <span className="select2-dropdown select2-dropdown--below">
                                      <span className="select2-results">
                                        <ul className="select2-results__options">
                                          { item.type === "product" && searchingProducts
                                            ?
                                            <li className="select2-results__option select2-results__message">
                                              <span className="spinner-border spinner-border-sm align-middle me-2"></span>
                                              {lang.labels.loading}...
                                            </li>
                                            :
                                            (item.type === "service" ? searchService : searchProduct).list.length > 0
                                            ?
                                            <>
                                              { (item.type === "service" ? searchService : searchProduct).list.map (( (other_item, other_index) => { return (
                                                <li key={other_index} className={`select2-results__option select2-results__option--selectable ${other_index === highlightedIndex || (highlightedIndex === -1 && (other_item.type === "service" ? other_item.id_service : other_item.id_variant ) === sub_item.reference) ? "select2-results__option--selected" : ""}`} onMouseEnter={() => setHighlightedIndex(other_index)}>
                                                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeSelectDetailContentToRequirement(index, sub_index, other_item)}>{formatContentDetail(other_item)}</Dropdown.Item>
                                                </li>
                                              )}))}
                                            </>
                                            :
                                            <>
                                              { (sub_item.reference ?? '').length > 0
                                                ?
                                                <li className="select2-results__option select2-results__option--selectable select2-results__option--selected">
                                                  <Dropdown.Item bsPrefix="select2-results__option__text">{contentReferenceSelected(item.type === "service" ? list.services : list.products, sub_item.reference, item.type)}</Dropdown.Item>
                                                </li>
                                                :
                                                <li className="select2-results__option select2-results__message">
                                                  {lang.labels.noRecordsFound}
                                                </li>
                                                }
                                            </>
                                          }
                                        </ul>
                                      </span>
                                    </span>
                                  </Dropdown.Menu>
                                </Dropdown>
                                <AppLegend attribute={{check: requirements.valid[index].contents[sub_index].detail, name: "requirement-content-detail", index: index, sub_index: sub_index}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                              </td>
                              <td valign="top">
                                <AppInputRequirementUnit companyForUser={companyForUser} settingsForCompany={settingsForCompany} content={{value: sub_item, type: item.type, index: index, sub_index: sub_index}} requirements={requirements} setRequirements={setRequirements} loadSettingsForCompany={loadSettingsForCompany}></AppInputRequirementUnit>
                              </td>
                              <td valign="top">
                                <input className="form-control px-2" type="text" autoComplete="off" name="quantity" value={sub_item.quantity} onChange={(event) => handleChangeQuantityContentToRequirement(index, sub_index, event)} onKeyUp={() => validateQuantityContentToRequirement(index, sub_index)} onBlur={() => validateQuantityContentToRequirement(index, sub_index)} />
                                <AppLegend attribute={{check: requirements.valid[index].contents[sub_index].quantity, name: "requirement-content-quantity", index: index, sub_index: sub_index}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                              </td>
                              <td valign="top">
                                <AppRequirementInputPrice content={{value: sub_item, index: index, sub_index: sub_index}} requirements={requirements} setRequirements={setRequirements} validateRebaja={validateRebaja}></AppRequirementInputPrice>
                              </td>
                              <td valign="top">
                                <AppInputRequirementTax companyForUser={companyForUser} settingsForCompany={settingsForCompany} type={"sale"} date={date} content={{value: sub_item, index: index, sub_index: sub_index}} requirements={requirements} setRequirements={setRequirements} validateRebaja={validateRebaja}></AppInputRequirementTax>
                              </td>
                              <td valign="top">
                                <div className="position-relative">
                                <input className="form-control ps-2 pe-7" type="text" autoComplete="off" name="rebate" disabled={(sub_item.reference ?? '').length > 0} value={sub_item.rebate_amount} onChange={(event) => handleChangeRebateContentToRequirement(index, sub_index, event)} onKeyUp={() => validateRebateContentToRequirement(index, sub_index)} onBlur={() => validateRebateContentToRequirement(index, sub_index)} />
                                  <i className="position-absolute translate-middle-y top-50 end-0 me-2 fs-7" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={(sub_item.reference ?? '').length === 0 ? lang.labels.enterRebate : `${lang.labels.rebate}: ${discountDescription(lang, currency, {type: sub_item.rebate_type, value: sub_item.rebate_value, quantity: sub_item.rebate_quantity})}`}><FaExclamationCircle /></i>
                                </div>
                                <AppLegend attribute={{check: requirements.valid[index].contents[sub_index].rebate_amount, name: "requirement-content-rebate", index: index, sub_index: sub_index}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                              </td>
                              <td valign="top"> 
                                <div className="d-flex justify-content-end">
                                  <label className="badge badge-lg badge-light-primary d-flex align-items-center justify-content-end text-nowrap w-100 ps-0 pe-2">
                                    {companyForUser.currency?.code} {formatMoney(calculatorAmount().requirement_content_prices[index][sub_index])}
                                  </label>
                                  <button className="btn btn-icon btn-flex btn-primary btn-light-primary ms-2" type="button" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="left" data-bs-original-title={lang.labels.removeContent} onClick={() => executeRemoveContentToRequirement(index, sub_index)}>
                                    <span className="svg-icon svg-icon-3 p-3">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                        <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                        <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                      </svg>
                                    </span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}))}
                          <tr>
                            <td colSpan={7}>
                              <div id={`legend-requirement-contents-required${suffixLegend(index)}`} className="fv-plugins-message-container invalid-feedback d-none">
                                <div data-field="contents" data-validator="notEmpty">{lang.labels.contentsToRequirementIsRequired}</div>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={7}>
                              <span className="d-flex justify-content-end">
                                <div className="text-uppercase text-secondary fw-bold fs-8">{lang.labels.priceTotal}:</div>
                                <div className="text-end fw-bolder ms-10">{companyForUser.currency?.code} {item.contents.length > 0 ? formatMoney(calculatorAmount().requirement_prices[index]) : formatMoney(0)}</div>
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={7}></td>
                          </tr>
                        </tbody>
                      )}))}
                    </>
                    :
                    <tbody>
                      <tr>
                        <td colSpan={7}>
                          <div className="form-control form-control-sm text-center text-uppercase">{lang.labels.noRecords}</div>
                        </td>
                      </tr>
                    </tbody>
                  }
                  <tfoot>
                    <tr>
                      <td colSpan={7}>
                        <div id="legend-requirements-required" className="fv-plugins-message-container invalid-feedback d-none">
                          <div data-field="requirements" data-validator="notEmpty">{lang.labels.requirementsIsRequired}</div>
                        </div>
                      </td>
                    </tr>
                    <tr className="border-top">
                      <td colSpan={7}>
                        <div className="d-flex justify-content-end">
                          <div className="mw-500px">
                            <div className="border-bottom mt-3">
                              <div className="d-flex align-items-center justify-content-end">
                                <span className="text-dark text-uppercase fw-bolder">{lang.labels.calculator}</span>
                                <span className="svg-icon svg-icon-1 ms-3" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-placement="left" data-bs-original-title={lang.labels.calculatedValuesAreReferential}>
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path opacity="0.3" d="M3 3V17H7V21H15V9H20V3H3Z" fill="black"/>
                                    <path d="M20 22H3C2.4 22 2 21.6 2 21V3C2 2.4 2.4 2 3 2H20C20.6 2 21 2.4 21 3V21C21 21.6 20.6 22 20 22ZM19 4H4V8H19V4ZM6 18H4V20H6V18ZM6 14H4V16H6V14ZM6 10H4V12H6V10ZM10 18H8V20H10V18ZM10 14H8V16H10V14ZM10 10H8V12H10V10ZM14 18H12V20H14V18ZM14 14H12V16H14V14ZM14 10H12V12H14V10ZM19 14H17V20H19V14ZM19 10H17V12H19V10Z" fill="black"/>
                                  </svg>
                                </span>
                              </div>
                            </div>
                            <div className="d-flex flex-stack px-5 py-2 border-bottom">
                              <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.subTotal}:</div>
                              <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().sub_total)}</div>
                            </div>
                            <div className="d-flex flex-stack px-5 py-2 border-bottom">
                              <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.rebateTotal}:</div>
                              <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().rebate_total)}</div>
                            </div>
                            <div className="d-flex flex-stack px-5 py-2 border-bottom">
                              <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.totalAfterRebates}:</div>
                              <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().after_rebate_total)}</div>
                            </div>
                            <div className="d-flex flex-stack px-5 py-2 border-bottom">
                              <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.taxTotal}:</div>
                              <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().tax_total)}</div>
                            </div>
                            <div className="d-flex flex-stack px-5 py-2 border-bottom">
                              <div className="fw-bold pe-20 text-secondary fs-7">{lang.labels.total}:</div>
                              <div className="text-end fw-bolder ps-20">{companyForUser.currency?.code} {formatMoney(calculatorAmount().total)}</div>
                            </div>
                            { foreignCurrency && foreignCurrency.enabled && foreignCurrency.exchange_rate && parseFloat(foreignCurrency.exchange_rate) > 0 &&
                              <div className="d-flex flex-stack px-5 py-2 border-bottom bg-light">
                                <div className="fw-bold pe-20 text-secondary fs-7">Total {foreignCurrency.code}:</div>
                                <div className="text-end fw-bolder ps-20">{foreignCurrency.code} {formatMoney(companyToForeign(calculatorAmount().total, foreignCurrency))}</div>
                              </div>
                            }
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      }
    </>
  )
};

export default AppSaleFormRequirements;